import { EnvelopeIcon, NewspaperIcon, PhoneIcon, UserGroupIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import { Tooltip } from 'flowbite-react';
import React from 'react';

import { formatDate, formatSurface } from '../../../../common-utils/formatters';
import { ContactInteractionType } from '../../../@types/contactInteraction';
import type { DpeTableData } from '../../../@types/DpeTableData';
import type { Owner } from '../../../@types/Owner';
import RedirectionCoproButtons from '../../common/RedirectionCoproButtons';
import RedirectionMapButtons from '../../common/RedirectionMapButtons';

interface DPEData {
  value: string;
  date?: string;
  surface?: number;
  floor?: string;
  infoDPE?: string;
  identifiantDpe?: string;
  floorDPE?: string;
  surfaceDPE?: number;
  dateDPE?: string;
  rawSurface?: number;
  rawDate?: string;
  details?: {
    identifiantDpe?: string;
  };
  [key: string]: unknown;
}

type DPEDetailsHandler = (details?: Record<string, unknown>) => void;

export const formatDPEDetails = (dataToFormat: DPEData, handleDetailClick: DPEDetailsHandler, handleAdsClick?: DPEDetailsHandler) => {
  const adjustedDPEData = { ...dataToFormat };
  // Flexibility to any endpoint formatting
  if (adjustedDPEData.floorDPE !== null && adjustedDPEData.floorDPE !== undefined && adjustedDPEData.floorDPE !== '') {
    adjustedDPEData.floor = adjustedDPEData.floorDPE;
  }
  if (adjustedDPEData.infoDPE) {
    adjustedDPEData['INFO DPE'] = adjustedDPEData.infoDPE;
  }
  if (adjustedDPEData.details && adjustedDPEData.details.identifiantDpe) {
    adjustedDPEData.identifiantDpe = adjustedDPEData.details.identifiantDpe;
  }
  if (adjustedDPEData.rawSurface != null) {
    adjustedDPEData.surface = Number(adjustedDPEData.rawSurface);
  }
  if (adjustedDPEData.rawDate != null) {
    adjustedDPEData.date = String(adjustedDPEData.rawDate);
  }

  return (
    <span>
      <b>DPE :</b>
      {' '}
      {adjustedDPEData.value}
      {' '}
      (date :
      {' '}
      {adjustedDPEData.date ? formatDate(adjustedDPEData.date) : 'Non renseignée'}
      )
      <br />
      <b>Surface :</b>
      {' '}
      {adjustedDPEData.surface ? `${formatSurface(Number(adjustedDPEData.surface))}` : 'Non renseignée'}
      <br />
      {adjustedDPEData.floor !== null && adjustedDPEData.floor !== undefined && adjustedDPEData.floor !== '' && (
        <>
          <b>Etage renseigné :</b>
          {' '}
          {adjustedDPEData.floor}
          {' '}
          <br />
        </>
      )}
      {adjustedDPEData['INFO DPE'] ? (
        <>
          <b>Info DPE :</b>
          {' '}
          {adjustedDPEData['INFO DPE']}
          <br />
        </>
      ) : null}
      <b>Identifiant :</b>
      {' '}
      {adjustedDPEData.identifiantDpe}
      <br />
      <div
        className="cursor-pointer text-rgpd-blue-light hover:underline"
        onClick={() => handleDetailClick(adjustedDPEData.details)}
      >
        (Voir le détail)
      </div>
      {handleAdsClick && (
        <div
          className="mt-1 flex cursor-pointer items-center gap-1 text-green-600 hover:underline"
          onClick={() => handleAdsClick(adjustedDPEData.details)}
        >
          Recherche annonces
        </div>
      )}
    </span>
  );
};

export const formatDpeData = (dataToFormat: DpeTableData[], handleDetailClick: DPEDetailsHandler, handleAdsClick?: DPEDetailsHandler) => dataToFormat.map((item) => ({
  ...item,
  'Nom IMMEUBLE': <div className="flex items-center">
    <RedirectionCoproButtons coproId={item.idCopro} />
    {item['Nom IMMEUBLE']}
  </div>,
  'Adresse IMMEUBLE': (
    item.banLabel
      ? (
        <div className="flex items-center">
          <div>
            <RedirectionMapButtons address={item.banLabel} />
          </div>
          <span>{item.banLabel}</span>
        </div>
      )
      : ''),
  'Caractéristiques à identifier': (
    formatDPEDetails(item as unknown as DPEData, handleDetailClick, handleAdsClick)
  ),
}));


export const getContactInteractionTypeIcon = (type: ContactInteractionType) => {
  switch (type) {
  case ContactInteractionType.PAPER:
    return (
      <Tooltip content="Par courrier">
        <span className="cursor-help">
          <NewspaperIcon className="h-5 w-5 text-gray-600" />
        </span>
      </Tooltip>
    );
  case ContactInteractionType.PHONE:
    return (
      <Tooltip content="Par appel téléphonique">
        <span className="cursor-help">
          <PhoneIcon className="h-5 w-5 text-gray-600" />
        </span>
      </Tooltip>
    );
  case ContactInteractionType.VISIO:
    return (
      <Tooltip content="Par visio">
        <span className="cursor-help">
          <VideoCameraIcon className="h-5 w-5 text-gray-600" />
        </span>
      </Tooltip>
    );
  case ContactInteractionType.PHYSICAL:
    return (
      <Tooltip content="En présentiel">
        <span className="cursor-help">
          <UserGroupIcon className="h-5 w-5 text-gray-600" />
        </span>
      </Tooltip>
    );
  case ContactInteractionType.MAIL:
    return (
      <Tooltip content="Par email">
        <span className="cursor-help">
          <EnvelopeIcon className="h-5 w-5 text-gray-600" />
        </span>
      </Tooltip>
    );
  default:
    return null;
  }
};

// Centralized function to format DPE details from an owner object
export const formatDpeDetailsFromOwner = (owner?: Owner, handleDetailClick?: DPEDetailsHandler, handleAdsClick?: DPEDetailsHandler): React.ReactNode => {
  if (!owner) {return 'Non renseigné';}
  // Incoming call owners don't have a DPE; display a meaningful label
  if (!owner.dpe && owner.incomingCallCopro) {
    return "Lead d'un appel entrant, pas de DPE";
  }
  if (!owner.dpe) {return 'Non renseigné';}
  
  // Prepare DPE data in the format expected by the formatter
  const dpeDetails = owner.dpe.details || {};
  
  let dpeDetail: Record<string, unknown> = {};
  if (Array.isArray(dpeDetails) && dpeDetails.length > 0) {
    dpeDetail = dpeDetails[0] as Record<string, unknown>;
  } else if (typeof dpeDetails === 'object' && dpeDetails !== null && Object.keys(dpeDetails).length > 0) {
    dpeDetail = dpeDetails as Record<string, unknown>;
  }
  
  const formattedDpe = {
    id: owner.dpe.id,
    value: String(dpeDetail.classeBilanDpe || dpeDetail.valeurDpe || owner.dpe.infoDPE?.class || 'Non identifié'),
    date: null as string | null,
    surface: null as number | null,
    rawDate: null as string | null,
    details: {
      ...dpeDetail,
      identifiantDpe: dpeDetail.identifiantDpe || owner.dpe.remoteId || '',
      remoteId: owner.dpe.remoteId,
    },
  };
  
  // Set date from various possible sources
  if (dpeDetail.dateReceptionDpe) {
    formattedDpe.rawDate = typeof dpeDetail.dateReceptionDpe === 'string' 
      ? dpeDetail.dateReceptionDpe.split(' ')[0] 
      : String(dpeDetail.dateReceptionDpe);
  } else if (dpeDetail.dateEtablissementDpe) {
    formattedDpe.rawDate = typeof dpeDetail.dateEtablissementDpe === 'string' 
      ? dpeDetail.dateEtablissementDpe.split(' ')[0] 
      : String(dpeDetail.dateEtablissementDpe);
  } else if (owner.dpe.establishedAt) {
    formattedDpe.rawDate = String(owner.dpe.establishedAt);
  } else if (owner.dpe.receivedAt) {
    formattedDpe.rawDate = String(owner.dpe.receivedAt);
  } else if (owner.dpe.createdAt) {
    formattedDpe.rawDate = String(owner.dpe.createdAt);
  }
  
  // Set surface
  if (dpeDetail.surfaceHabitableLogement) {
    try {
      const surfaceValue = parseFloat(dpeDetail.surfaceHabitableLogement as string);
      if (!isNaN(surfaceValue)) {
        formattedDpe.surface = surfaceValue;
      }
    } catch (e) {
      console.error('Error parsing surface:', e);
    }
  } else if (owner.surface) {
    formattedDpe.surface = owner.surface;
  }
  
  return formatDPEDetails(formattedDpe as DPEData, handleDetailClick, handleAdsClick);
};

export const formatDateFromIsoString = (isoString: string | undefined | null): string => {
  if (!isoString) {return '-';}
  const date = new Date(isoString.split('+')[0]);
  if (isNaN(date.getTime())) {return '-';}
  return date.toLocaleDateString('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};
